import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Button, CircularProgress, TextField, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Chip, TablePagination,
  FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab, Box,
  Avatar, Tooltip, IconButton,
  Accordion, AccordionSummary, AccordionDetails, Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Notification, useNotify } from "react-admin";
import { getConfigGet, getConfigPost, getConfigPatch, getConfigDelete } from "../getConfig";
import ENDPOINTS from "../apiConstants";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex", flexDirection: "column", minHeight: "100vh",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#f5f5f5",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    marginBottom: theme.spacing(3), flexWrap: "wrap", gap: theme.spacing(2),
  },
  title: { fontWeight: 600 },
  subtitle: { color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
  filterContainer: {
    display: "flex", gap: theme.spacing(2), flexWrap: "wrap", alignItems: "flex-end",
    marginBottom: theme.spacing(2), padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper, borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  filterField: { minWidth: 180 },
  tableContainer: { borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  tableHead: { backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : "#fafafa" },
  tableHeadCell: { fontWeight: 600, color: theme.palette.text.primary },
  brandCell: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
  brandLogo: { width: 40, height: 40, objectFit: "contain", borderRadius: 4, backgroundColor: "#fff", border: "1px solid #e0e0e0" },
  brandName: { fontWeight: 500 },
  overrideActive: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 500 },
  overrideInactive: { backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa", color: "#9e9e9e" },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" },
  dialogContent: { padding: theme.spacing(2), minWidth: 550 },
  dialogField: { marginBottom: theme.spacing(2) },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1.5), marginTop: theme.spacing(2), color: theme.palette.text.primary },
  referenceSection: { backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5", borderRadius: 8, padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  referenceTitle: { fontWeight: 600, marginBottom: theme.spacing(1), color: theme.palette.text.secondary, fontSize: 12, textTransform: "uppercase" },
  referenceItem: { display: "flex", justifyContent: "space-between", padding: theme.spacing(0.5, 0), fontSize: 13 },
  tabPanel: { padding: theme.spacing(2, 0) },
  accordionRoot: { marginBottom: theme.spacing(1), "&:before": { display: "none" }, borderRadius: "8px !important", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  accordionSummary: { backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa", borderRadius: 8 },
  jsonField: { fontFamily: "monospace", fontSize: 12 },
}));

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface Brand {
  id: number;
  external_key: string;
  display_name: string;
  logo_url: string | null;
  provider_name: string;
  status: string;
  discount_rate: number | null;
  sprive_haircut_percentage: number | null;
  override_id: number | null;
  display_name_override: string | null;
  logo_override: string | null;
  discount_override_percentage: number | null;
  discount_override_from_date: string | null;
  discount_override_to_date: string | null;
  sprive_haircut_override_percentage: number | null;
  sprive_haircut_override_from_date: string | null;
  sprive_haircut_override_to_date: string | null;
  boost_haircut_override_percentage: number | null;
  boosted_discount: number | null;
  recurring_purchase_subscription: boolean;
  description: string | null;
  expiry: string | null;
  digital_face_value_limits_lower: number | null;
  digital_face_value_limits_upper: number | null;
  terms_url: string | null;
  redemption_instructions_url: string | null;
  balance_enquiry_url: string | null;
  discount_override_active: boolean;
  sprive_haircut_override_active: boolean;
  effective_discount: number | null;
  effective_sprive_haircut: number | null;
  overall_discount: number | null;
}

interface BrandDetails extends Brand {
  terms_override: string | null;
  return_policy: string | null;
  redemption_methods_override: any | null;
  redemption_instruction: any | null;
  exclusions: any | null;
  categories_override: any | null;
  vdx_description: string | null;
  expiry_months: number | null;
  website_url: string | null;
}

interface Pagination {
  page: number; page_size: number; total: number;
  total_pages: number; has_next_page: boolean; has_previous_page: boolean;
}

interface BrandMetadata {
  total_brands: number;
  enabled_count: number;
}

interface EditFormData {
  display_name_override: string;
  logo_override: string;
  discount_override_percentage: string;
  discount_override_from_date: string;
  discount_override_to_date: string;
  sprive_haircut_override_percentage: string;
  sprive_haircut_override_from_date: string;
  sprive_haircut_override_to_date: string;
  boost_haircut_override_percentage: string;
  terms_override: string;
  return_policy: string;
  redemption_methods_override: string;
  redemption_instruction: string;
  exclusions: string;
  categories_override: string;
}

const BrandCatalogTilloOverride = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1, page_size: 10, total: 0, total_pages: 0,
    has_next_page: false, has_previous_page: false,
  });
  const [metadata, setMetadata] = useState<BrandMetadata>({ total_brands: 0, enabled_count: 0 });

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("display_name");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [providerFilter, setProviderFilter] = useState("1");
  const [hasMapping, setHasMapping] = useState("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editTabValue, setEditTabValue] = useState(0);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    display_name_override: "", logo_override: "",
    discount_override_percentage: "", discount_override_from_date: "", discount_override_to_date: "",
    sprive_haircut_override_percentage: "", sprive_haircut_override_from_date: "", sprive_haircut_override_to_date: "",
    boost_haircut_override_percentage: "", terms_override: "", return_policy: "",
    redemption_methods_override: "", redemption_instruction: "", exclusions: "", categories_override: "",
  });

  useEffect(() => { fetchBrands(); }, [pagination.page, pagination.page_size, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const params: any = { page: pagination.page, pageSize: pagination.page_size, sortBy, sortOrder };
      if (search) params.search = search;
      if (providerFilter) params.provider_id = providerFilter;
      if (hasMapping) params.has_mapping = hasMapping;
      const response: any = await axios.get(ENDPOINTS.BRAND_CATALOG, getConfigGet(params));
      if (response.data.success) {
        setBrands(response.data.data);
        const p = response.data.pagination;
        setPagination({
          page: p.page || 1, page_size: p.page_size || p.pageSize || 10,
          total: p.total, total_pages: p.total_pages || p.totalPages || 0,
          has_next_page: p.has_next_page || false, has_previous_page: p.has_previous_page || false,
        });
        if (response.data.metadata) setMetadata(response.data.metadata);
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to load brands", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandDetails = async (brandId: number) => {
    try {
      setLoadingDetails(true);
      const response: any = await axios.get(`${ENDPOINTS.BRAND_CATALOG}/${brandId}`, getConfigGet({}));
      if (response.data.success) {
        const brand = response.data.data;
        setSelectedBrand(brand);
        setEditFormData({
          display_name_override: brand.display_name_override || "",
          logo_override: brand.logo_override || "",
          discount_override_percentage: brand.discount_override_percentage?.toString() || "",
          discount_override_from_date: brand.discount_override_from_date?.split("T")[0] || "",
          discount_override_to_date: brand.discount_override_to_date?.split("T")[0] || "",
          sprive_haircut_override_percentage: brand.sprive_haircut_override_percentage?.toString() || "",
          sprive_haircut_override_from_date: brand.sprive_haircut_override_from_date?.split("T")[0] || "",
          sprive_haircut_override_to_date: brand.sprive_haircut_override_to_date?.split("T")[0] || "",
          boost_haircut_override_percentage: brand.boost_haircut_override_percentage?.toString() || "",
          terms_override: brand.terms_override || "",
          return_policy: brand.return_policy || "",
          redemption_methods_override: brand.redemption_methods_override ? JSON.stringify(brand.redemption_methods_override, null, 2) : "",
          redemption_instruction: brand.redemption_instruction ? JSON.stringify(brand.redemption_instruction, null, 2) : "",
          exclusions: brand.exclusions ? JSON.stringify(brand.exclusions, null, 2) : "",
          categories_override: brand.categories_override ? JSON.stringify(brand.categories_override, null, 2) : "",
        });
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to load brand details", "error");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSearch = () => { setPagination({ ...pagination, page: 1 }); fetchBrands(); };
  const handleClearFilters = () => {
    setSearch(""); setSortBy("display_name"); setSortOrder("ASC"); setProviderFilter("1"); setHasMapping("");
    setPagination({ ...pagination, page: 1 });
    setTimeout(() => fetchBrands(), 100);
  };
  const handlePageChange = (_event: unknown, newPage: number) => setPagination({ ...pagination, page: newPage + 1 });
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setPagination({ ...pagination, page_size: parseInt(event.target.value, 10), page: 1 });

  const handleEditClick = (brand: Brand) => { setEditTabValue(0); setEditDialogOpen(true); fetchBrandDetails(brand.id); };
  const handleEditClose = () => { setEditDialogOpen(false); setSelectedBrand(null); setEditTabValue(0); };
  const setField = (key: keyof EditFormData, value: any) => setEditFormData({ ...editFormData, [key]: value });

  const validateJsonField = (value: string, fieldName: string): boolean => {
    if (!value || value.trim() === "") return true;
    try { JSON.parse(value); return true; } catch { notify(`${fieldName} must be valid JSON`, "error"); return false; }
  };

  const handleSave = async () => {
    if (!selectedBrand) return;
    if (
      !validateJsonField(editFormData.redemption_methods_override, "Redemption Methods") ||
      !validateJsonField(editFormData.redemption_instruction, "Redemption Instruction") ||
      !validateJsonField(editFormData.exclusions, "Exclusions") ||
      !validateJsonField(editFormData.categories_override, "Categories Override")
    ) return;

    setSaving(true);
    try {
      const payload: any = {};
      if (editFormData.display_name_override) payload.display_name_override = editFormData.display_name_override;
      if (editFormData.logo_override) payload.logo_override = editFormData.logo_override;
      payload.discount_override_percentage = editFormData.discount_override_percentage !== "" ? parseFloat(editFormData.discount_override_percentage) : null;
      payload.discount_override_from_date = editFormData.discount_override_from_date || null;
      payload.discount_override_to_date = editFormData.discount_override_to_date || null;
      payload.sprive_haircut_override_percentage = editFormData.sprive_haircut_override_percentage !== "" ? parseFloat(editFormData.sprive_haircut_override_percentage) : null;
      payload.sprive_haircut_override_from_date = editFormData.sprive_haircut_override_from_date || null;
      payload.sprive_haircut_override_to_date = editFormData.sprive_haircut_override_to_date || null;
      payload.boost_haircut_override_percentage = editFormData.boost_haircut_override_percentage !== "" ? parseFloat(editFormData.boost_haircut_override_percentage) : null;
      if (editFormData.terms_override) payload.terms_override = editFormData.terms_override;
      if (editFormData.return_policy) payload.return_policy = editFormData.return_policy;
      if (editFormData.redemption_methods_override) payload.redemption_methods_override = JSON.parse(editFormData.redemption_methods_override);
      if (editFormData.redemption_instruction) payload.redemption_instruction = JSON.parse(editFormData.redemption_instruction);
      if (editFormData.exclusions) payload.exclusions = JSON.parse(editFormData.exclusions);
      if (editFormData.categories_override) payload.categories_override = JSON.parse(editFormData.categories_override);

      let response: any;
      if (selectedBrand.override_id) {
        response = await axios.patch(`${ENDPOINTS.BRAND_CATALOG}/${selectedBrand.override_id}`,
          { action: "override_update", ...payload }, { headers: getConfigPatch() });
      } else {
        response = await axios.post(ENDPOINTS.BRAND_CATALOG,
          { action: "override_save", brand_id: selectedBrand.id, ...payload }, { headers: getConfigPost() });
      }

      if (response.data.success) {
        notify("Brand override saved", "info");
        handleEditClose();
        fetchBrands();
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to update brand", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleClearOverrides = async () => {
    if (!selectedBrand?.override_id) return;
    setSaving(true);
    try {
      const response: any = await axios.delete(
        `${ENDPOINTS.BRAND_CATALOG}/${selectedBrand.override_id}?action=override`,
        { headers: getConfigDelete() }
      );
      if (response.data.success) {
        notify("Overrides cleared", "info");
        handleEditClose();
        fetchBrands();
      }
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to clear overrides", "error");
    } finally {
      setSaving(false);
    }
  };

  const formatPercentage = (value: number | null) => value == null ? "-" : `${value}%`;

  const isDiscountOverrideActive = (brand: Brand) => Boolean(brand.discount_override_active);

  const isHaircutOverrideActive = (brand: Brand) => Boolean(brand.sprive_haircut_override_active);

  const getEffectiveDiscount = (brand: Brand): number | null => brand.effective_discount ?? null;

  const getEffectiveHaircut = (brand: Brand): number | null => brand.effective_sprive_haircut ?? null;

  const getOverallDiscount = (brand: Brand): number | null => brand.overall_discount ?? null;

  const getBoostedDiscount = (brand: Brand): number | null => brand.boosted_discount ?? null;

  const jsonFields = [
    { label: "Redemption Methods Override", key: "redemption_methods_override" as keyof EditFormData, placeholder: '{"method": "value"}' },
    { label: "Redemption Instruction", key: "redemption_instruction" as keyof EditFormData, placeholder: '["step1", "step2"]' },
    { label: "Exclusions", key: "exclusions" as keyof EditFormData, placeholder: '["exclusion1", "exclusion2"]' },
    { label: "Categories Override", key: "categories_override" as keyof EditFormData, placeholder: '["category1", "category2"]' },
  ];

  if (loading && brands.length === 0) return <div className={classes.loadingContainer}><CircularProgress size={40} /></div>;

  return (
    <div className={classes.main}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" className={classes.title}>Brand Catalog Management</Typography>
          <Typography variant="body2" className={classes.subtitle}>Manage brand overrides, discounts, haircuts, and content</Typography>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ backgroundColor: "white", borderRadius: 8, padding: "16px 24px", minWidth: 150, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 24, fontWeight: 600, color: "#e91e8c" }}>{metadata.total_brands}</div>
          <div style={{ fontSize: 12, color: "#999", textTransform: "uppercase" }}>Total Brands</div>
        </div>
        <div style={{ backgroundColor: "white", borderRadius: 8, padding: "16px 24px", minWidth: 150, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
          <div style={{ fontSize: 24, fontWeight: 600, color: "#e91e8c" }}>{metadata.enabled_count}</div>
          <div style={{ fontSize: 12, color: "#999", textTransform: "uppercase" }}>Enabled</div>
        </div>
      </div>

      <Paper style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: "#f7f9fc" }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Boosted % explanation</Typography>
        <Typography variant="body2" color="textSecondary">
          Overall Discount is the cashback users get right now. Boosted % is the cashback users get when Boost is active. If a brand has a Boost haircut set, that is used first; otherwise the default Boost haircut from the active campaign is used.
        </Typography>
      </Paper>

      <div className={classes.filterContainer}>
        <TextField label="Search" variant="outlined" size="small" value={search}
          onChange={(e) => setSearch(e.target.value)} className={classes.filterField}
          placeholder="Search by brand name..." onKeyPress={(e) => e.key === "Enter" && handleSearch()} />
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Sort By</InputLabel>
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value as string)} label="Sort By">
            <MenuItem value="display_name">Brand Name</MenuItem>
            <MenuItem value="discount_rate">Discount</MenuItem>
            <MenuItem value="sprive_haircut_percentage">Haircut</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Sort Order</InputLabel>
          <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as "ASC" | "DESC")} label="Sort Order">
            <MenuItem value="ASC">Ascending</MenuItem>
            <MenuItem value="DESC">Descending</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Has Mapping</InputLabel>
          <Select value={hasMapping} onChange={(e) => setHasMapping(e.target.value as string)} label="Has Mapping">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterField}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value as string)} label="Provider">
            <MenuItem value="1">Tillo</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" color="primary" onClick={handleSearch} disabled={loading}>Search</Button>
        <Button variant="outlined" onClick={handleClearFilters}>Clear</Button>
        <IconButton onClick={fetchBrands} disabled={loading}><RefreshIcon /></IconButton>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Brand</TableCell>
              <TableCell className={classes.tableHeadCell}>Base Discount</TableCell>
              <TableCell className={classes.tableHeadCell}>Effective Discount</TableCell>
              <TableCell className={classes.tableHeadCell}>Base Haircut</TableCell>
              <TableCell className={classes.tableHeadCell}>Effective Haircut</TableCell>
              <TableCell className={classes.tableHeadCell}>Overall Discount</TableCell>
              <TableCell className={classes.tableHeadCell}>
                <Tooltip title="Boosted % shows customer cashback when boost is active." placement="top">
                  <span>Boosted %</span>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.map((brand) => (
              <TableRow key={brand.id} hover>
                <TableCell>
                  <div className={classes.brandCell}>
                    <Avatar src={brand.logo_override || brand.logo_url || ""} alt={brand.display_name} className={classes.brandLogo} variant="square" />
                    <div>
                      <div className={classes.brandName}>{brand.display_name_override || brand.display_name}</div>
                      {brand.display_name_override && <div style={{ fontSize: 11, color: "#999" }}>({brand.display_name})</div>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{formatPercentage(brand.discount_rate)}</TableCell>
                <TableCell>
                  <Chip label={formatPercentage(getEffectiveDiscount(brand))} size="small"
                    className={isDiscountOverrideActive(brand) ? classes.overrideActive : classes.overrideInactive} />
                </TableCell>
                <TableCell>{formatPercentage(brand.sprive_haircut_percentage)}</TableCell>
                <TableCell>
                  <Chip label={formatPercentage(getEffectiveHaircut(brand))} size="small"
                    className={isHaircutOverrideActive(brand) ? classes.overrideActive : classes.overrideInactive} />
                </TableCell>
                <TableCell>{formatPercentage(getOverallDiscount(brand))}</TableCell>
                <TableCell>{formatPercentage(getBoostedDiscount(brand))}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit Brand">
                    <IconButton size="small" color="primary" onClick={() => handleEditClick(brand)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          count={pagination.total} page={pagination.page - 1}
          onChangePage={handlePageChange} rowsPerPage={pagination.page_size}
          onChangeRowsPerPage={handleRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {selectedBrand && <Avatar src={selectedBrand.logo_override || selectedBrand.logo_url || ""} variant="square" style={{ width: 32, height: 32 }} />}
            <div>
              Edit Brand Configuration
              {selectedBrand && <Typography variant="body2" color="textSecondary">{selectedBrand.display_name_override || selectedBrand.display_name}</Typography>}
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loadingDetails ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><CircularProgress /></div>
          ) : selectedBrand ? (
            <>
              <div className={classes.referenceSection}>
                <div className={classes.referenceTitle}>Base Values (Read-only)</div>
                <div className={classes.referenceItem}><span>Base Discount:</span><span>{formatPercentage(selectedBrand.discount_rate)}</span></div>
                <div className={classes.referenceItem}><span>Base Haircut:</span><span>{formatPercentage(selectedBrand.sprive_haircut_percentage)}</span></div>
                <div className={classes.referenceItem}><span>Status:</span><span>{selectedBrand.status}</span></div>
                <div className={classes.referenceItem}><span>Provider:</span><span>{selectedBrand.provider_name}</span></div>
              </div>

              <Tabs value={editTabValue} onChange={(_e, v) => setEditTabValue(v)} indicatorColor="primary" textColor="primary">
                <Tab label="Overrides" />
                <Tab label="Content" />
                <Tab label="Settings" />
              </Tabs>

              <TabPanel value={editTabValue} index={0}>
                <div className={classes.tabPanel}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>Brand Identity Override</Typography>
                  <TextField className={classes.dialogField} label="Display Name Override" variant="outlined" fullWidth size="small"
                    value={editFormData.display_name_override} onChange={(e) => setField("display_name_override", e.target.value)}
                    helperText="Override the display name for this brand" />
                  <TextField className={classes.dialogField} label="Logo URL Override" variant="outlined" fullWidth size="small"
                    value={editFormData.logo_override} onChange={(e) => setField("logo_override", e.target.value)}
                    helperText="Override the logo URL for this brand" />
                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="subtitle2" className={classes.sectionTitle}>Discount Override</Typography>
                  <TextField className={classes.dialogField} label="Discount Override Percentage" variant="outlined" fullWidth size="small" type="number"
                    value={editFormData.discount_override_percentage} onChange={(e) => setField("discount_override_percentage", e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    helperText="Leave empty to use base discount" />
                  <div style={{ display: "flex", gap: 16 }}>
                    <TextField className={classes.dialogField} label="From Date" variant="outlined" fullWidth size="small" type="date"
                      value={editFormData.discount_override_from_date} onChange={(e) => setField("discount_override_from_date", e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                    <TextField className={classes.dialogField} label="To Date" variant="outlined" fullWidth size="small" type="date"
                      value={editFormData.discount_override_to_date} onChange={(e) => setField("discount_override_to_date", e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                  </div>
                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="subtitle2" className={classes.sectionTitle}>Sprive Haircut Override</Typography>
                  <TextField className={classes.dialogField} label="Haircut Override Percentage" variant="outlined" fullWidth size="small" type="number"
                    value={editFormData.sprive_haircut_override_percentage} onChange={(e) => setField("sprive_haircut_override_percentage", e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    helperText="Leave empty to use base haircut" />
                  <div style={{ display: "flex", gap: 16 }}>
                    <TextField className={classes.dialogField} label="From Date" variant="outlined" fullWidth size="small" type="date"
                      value={editFormData.sprive_haircut_override_from_date} onChange={(e) => setField("sprive_haircut_override_from_date", e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                    <TextField className={classes.dialogField} label="To Date" variant="outlined" fullWidth size="small" type="date"
                      value={editFormData.sprive_haircut_override_to_date} onChange={(e) => setField("sprive_haircut_override_to_date", e.target.value)}
                      InputLabelProps={{ shrink: true }} />
                  </div>
                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="subtitle2" className={classes.sectionTitle}>Boost Haircut Override</Typography>
                  <TextField className={classes.dialogField} label="Boost Haircut Override Percentage" variant="outlined" fullWidth size="small" type="number"
                    value={editFormData.boost_haircut_override_percentage} onChange={(e) => setField("boost_haircut_override_percentage", e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    helperText="Haircut applied when user has active boost. Leave empty for global boost haircut." />
                </div>
              </TabPanel>

              <TabPanel value={editTabValue} index={1}>
                <div className={classes.tabPanel}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>Terms & Policies</Typography>
                  <TextField className={classes.dialogField} label="Terms & Conditions Override" variant="outlined" fullWidth size="small" multiline rows={3}
                    value={editFormData.terms_override} onChange={(e) => setField("terms_override", e.target.value)} />
                  <TextField className={classes.dialogField} label="Return Policy" variant="outlined" fullWidth size="small" multiline rows={3}
                    value={editFormData.return_policy} onChange={(e) => setField("return_policy", e.target.value)} />
                  <Divider style={{ margin: "16px 0" }} />
                  <Typography variant="subtitle2" className={classes.sectionTitle}>JSON Fields</Typography>
                  {jsonFields.map((f) => (
                    <Accordion key={f.key} className={classes.accordionRoot}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                        <Typography>{f.label}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TextField fullWidth multiline rows={5} variant="outlined" size="small"
                          value={String(editFormData[f.key] || "")} onChange={(e) => setField(f.key, e.target.value)}
                          className={classes.jsonField} placeholder={f.placeholder} />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </div>
              </TabPanel>

              <TabPanel value={editTabValue} index={2}>
                <div className={classes.tabPanel}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>Brand Information (Read-only)</Typography>
                  <div className={classes.referenceSection}>
                    {selectedBrand.description && (
                      <>
                        <div className={classes.referenceItem}><span>Description:</span></div>
                        <Typography variant="body2" style={{ marginTop: 4 }}>{selectedBrand.description}</Typography>
                      </>
                    )}
                    {selectedBrand.expiry && (
                      <div className={classes.referenceItem} style={{ marginTop: 12 }}>
                        <span>Expiry:</span><span>{selectedBrand.expiry}</span>
                      </div>
                    )}
                    {selectedBrand.digital_face_value_limits_lower != null && (
                      <div className={classes.referenceItem}>
                        <span>Face Value Range:</span>
                        <span>£{selectedBrand.digital_face_value_limits_lower} - £{selectedBrand.digital_face_value_limits_upper}</span>
                      </div>
                    )}
                    {selectedBrand.website_url && (
                      <div className={classes.referenceItem}>
                        <span>Website:</span>
                        <a href={selectedBrand.website_url} target="_blank" rel="noopener noreferrer">{selectedBrand.website_url}</a>
                      </div>
                    )}
                    {selectedBrand.terms_url && (
                      <div className={classes.referenceItem}>
                        <span>Terms:</span>
                        <a href={selectedBrand.terms_url} target="_blank" rel="noopener noreferrer">{selectedBrand.terms_url}</a>
                      </div>
                    )}
                    {selectedBrand.redemption_instructions_url && (
                      <div className={classes.referenceItem}>
                        <span>Redemption Instructions:</span>
                        <a href={selectedBrand.redemption_instructions_url} target="_blank" rel="noopener noreferrer">{selectedBrand.redemption_instructions_url}</a>
                      </div>
                    )}
                    {selectedBrand.balance_enquiry_url && (
                      <div className={classes.referenceItem}>
                        <span>Balance Enquiry:</span>
                        <a href={selectedBrand.balance_enquiry_url} target="_blank" rel="noopener noreferrer">{selectedBrand.balance_enquiry_url}</a>
                      </div>
                    )}
                  </div>
                </div>
              </TabPanel>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selectedBrand?.override_id && (
            <Button onClick={handleClearOverrides} color="secondary" disabled={saving || loadingDetails}>
              Clear All Overrides
            </Button>
          )}
          <Button onClick={handleEditClose} color="default">Cancel</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving || loadingDetails}>
            {saving && <CircularProgress size={16} style={{ marginRight: 8 }} />}
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default BrandCatalogTilloOverride;
